import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import {
    EMPTY,
    map,
    Observable,
    of,
    Subject,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs';
import { AccessConstants } from '../../../../../../core/constants/access-constants';
import { AuthorizeNetPaymentTransactionStatus } from '../../../../../../core/enums/commerce/payment/authorize-net-payment-transaction-status';
import { OrderPaymentTransactionStatus } from '../../../../../../core/enums/commerce/payment/order-payment-transaction-status';
import { PaymentMethod } from '../../../../../../core/enums/commerce/payment/payment-method';
import { AuthorizeNetPaymentTransactionStatusResponse } from '../../../../../../core/models/commerce/payment/authorize-net-payment-transaction-status-response';
import { AlertService } from '../../../../../../core/services/alert/alert.service';
import { ModalComponent } from '../../../../../../shared/components/modal/modal.component';
import { UserStateService } from '../../../../../user/shared/services/user-state/user-state.service';
import { OrderStatus } from '../../../shared/constants/order-status';
import { OrderService } from '../../../shared/services/order/order.service';
import { PaymentService } from '../../../shared/services/payment/payment.service';
import { PaymentHistoryGridRowData } from '../../models/payment-history-grid-row-data';
import { ConfirmPaymentFormDialogComponent } from './components/confirm-payment-form-dialog/confirm-payment-form-dialog.component';
import { CreditPaymentFormDialogComponent } from './components/credit-payment-form-dialog/credit-payment-form-dialog.component';
import {
    ConfirmPaymentTransactionModalConstants,
    CreditPaymentTransactionModalConstants,
    SendInvoiceModalConstants,
    VoidPaymentTransactionModalConstants,
} from './constants/payment-operation-cell-renderer-modal-constants';
import { PaymentOperationsCellRendererParams } from './constants/payment-operations-cell-renderer-params';

@Component({
    selector: 'app-payment-operations-cell-renderer',
    templateUrl: './payment-operations-cell-renderer.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [],
})
export class PaymentOperationsCellRendererComponent
    implements ICellRendererAngularComp, OnDestroy
{
    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly dialog: MatDialog,
        private readonly alertService: AlertService,
        private readonly orderService: OrderService,
        private readonly paymentService: PaymentService,
        private readonly userStateService: UserStateService,
    ) {}

    params: PaymentOperationsCellRendererParams =
        {} as PaymentOperationsCellRendererParams;

    agInit(
        params: ICellRendererParams<PaymentHistoryGridRowData> &
            PaymentOperationsCellRendererParams,
    ): void {
        this.params = params as PaymentOperationsCellRendererParams;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    refresh(): boolean {
        return false;
    }

    get orderId(): string | null {
        return this.params.orderId;
    }

    get paymentId(): string | null {
        return this.params.data?.id.toString() || null;
    }

    get balance(): number {
        return this.params.balance;
    }

    get invoiceSent(): boolean {
        return Boolean(this.params.invoicedAt);
    }

    /**
     * Only display the void credit action for approved Authorize.NET transactions, as this is the only scenario where voiding a payment is applicable.
     * The transaction must have the following characteristics:
     * - Payment Method: Authorize.NET AIM (Advanced Integration Method)
     * - Transaction Status: Approved
     *
     * @returns boolean indicating whether the void credit action should be displayed for the current grid row
     */
    displayVoidCreditAction(): boolean {
        return (
            this.params.data?.paymentMethod === PaymentMethod.AuthNetAim &&
            this.params.data?.status === OrderPaymentTransactionStatus.Approved
        );
    }

    /**
     * Only display the confirm payment action for eligible Invoice Net 30 transactions, as this is the only scenario where confirming a payment is applicable.
     * The transaction must have the following characteristics:
     * - Order Status: Invoiced
     * - Payment Method: Invoice Net 30
     * - Transaction Status: Pending
     *
     * The current authenticated user must also have the appropriate elevated role to perform this action.
     *
     * @returns boolean indicating whether the confirm payment action should be displayed for the current grid row
     */
    displayConfirmPaymentAction(): boolean {
        const authenticatedUser = this.userStateService.getUser();

        if (!authenticatedUser) {
            throw new Error('User is not authenticated');
        }

        return (
            AccessConstants.RolesThatCanManageAllPayments.includes(
                authenticatedUser.role,
            ) &&
            (this.params.orderStatus === OrderStatus.Invoiced ||
                this.params.orderStatus === OrderStatus.ExportedToAccounting) &&
            this.params.data?.paymentMethod === PaymentMethod.InvoiceNet30 &&
            this.params.data?.status === OrderPaymentTransactionStatus.Pending
        );
    }

    displaySendInvoiceAction(): boolean {
        const authenticatedUser = this.userStateService.getUser();

        if (!authenticatedUser) {
            throw new Error('User is not authenticated');
        }

        return (
            AccessConstants.RolesThatCanManagePayments.includes(
                authenticatedUser.role,
            ) &&
            this.params.orderStatus === OrderStatus.Pending &&
            this.params.data?.paymentMethod === PaymentMethod.InvoiceNet30 &&
            this.params.data?.status === OrderPaymentTransactionStatus.Pending
        );
    }

    /**
     * Initiates the void or credit process for an Authorize.NET payment transaction based on its current status.
     */
    voidCredit(): void {
        this.validateOrderAndPaymentIds(this.orderId, this.paymentId);

        this.paymentService
            .getStatus(this.orderId!, this.paymentId!)
            .pipe(
                takeUntil(this.destroy$),
                switchMap(
                    (
                        response: AuthorizeNetPaymentTransactionStatusResponse,
                    ) => {
                        switch (response.status) {
                            case AuthorizeNetPaymentTransactionStatus.Voidable:
                                return this.handleVoidableTransaction();
                            case AuthorizeNetPaymentTransactionStatus.Creditable:
                                return this.handleCreditableTransaction();
                            default:
                                // If the transaction is neither voidable nor creditable, do not display
                                // any action dialog because there are no applicable operations.
                                return EMPTY;
                        }
                    },
                ),
            )
            .subscribe();
    }

    /**
     * Opens a dialog containing the confirm payment form to allow the user to enter the necessary information to confirm the payment transaction.
     */
    confirmPayment(): void {
        this.validateOrderAndPaymentIds(this.orderId, this.paymentId);

        this.dialog
            .open(ConfirmPaymentFormDialogComponent, {
                width: '650px',
                disableClose: true,
                data: {
                    orderId: this.orderId,
                    paymentId: this.paymentId,
                    balance: this.balance,
                },
            })
            .afterClosed()
            .pipe(
                takeUntil(this.destroy$),
                switchMap((confirmed: boolean) => {
                    if (confirmed) {
                        return of(confirmed);
                    }

                    // Terminate the stream without performing any additional actions since the
                    // user canceled the confirm operation.
                    return EMPTY;
                }),
                tap(() => {
                    this.params.onActionComplete();

                    this.alertService.displaySuccessAlert(
                        ConfirmPaymentTransactionModalConstants.SUCCESS_MESSAGE,
                    );
                }),
            )
            .subscribe();
    }

    sendInvoice(): void {
        this.openSendInvoiceConfirmationDialog()
            .pipe(
                takeUntil(this.destroy$),
                switchMap((confirmed: boolean) => {
                    if (confirmed) {
                        return this.orderService.sendInvoice(this.orderId!);
                    }

                    // Terminate the stream without performing any additional actions since the
                    // user canceled the send invoice operation.
                    return EMPTY;
                }),
                tap(() => {
                    this.params.onActionComplete();

                    this.alertService.displaySuccessAlert(
                        SendInvoiceModalConstants.SUCCESS_MESSAGE,
                    );
                }),
            )
            .subscribe();
    }

    /**
     * Opens a confirmation dialog to confirm the user's intent to void the payment transaction.
     *
     * @return An Observable that emits a boolean value indicating whether the user confirmed the void action (true) or canceled it (false).
     */
    private openVoidConfirmationDialog(): Observable<boolean> {
        return this.dialog
            .open(ModalComponent, {
                disableClose: true,
                data: {
                    title: VoidPaymentTransactionModalConstants.CONFIRMATION_DIALOG_TITLE,
                    content:
                        VoidPaymentTransactionModalConstants.CONFIRMATION_DIALOG_CONTENT,
                    primaryAction: {
                        title: VoidPaymentTransactionModalConstants.PRIMARY_ACTION_TITLE,
                    },
                    isShowCancel: true,
                },
            })
            .afterClosed();
    }

    /**
     * Handles the voiding of a payment transaction that is in a voidable state.
     *
     * @returns An Observable that completes when the void operation is finished, or is empty if the user cancels the action
     */
    private handleVoidableTransaction(): Observable<boolean> {
        this.validateOrderAndPaymentIds(this.orderId, this.paymentId);

        return this.openVoidConfirmationDialog().pipe(
            switchMap((confirmed: boolean) => {
                if (confirmed) {
                    return this.paymentService
                        .void(this.orderId!, this.paymentId!)
                        .pipe(map(() => confirmed));
                }

                // Terminate the stream without performing any additional actions
                // since the user canceled the void operation.
                return EMPTY;
            }),
            tap(() => {
                this.params.onActionComplete();

                this.alertService.displaySuccessAlert(
                    VoidPaymentTransactionModalConstants.SUCCESS_MESSAGE,
                );
            }),
        );
    }

    /**
     * Opens a dialog containing the credit payment form to allow the user to enter the necessary information to process a credit for the payment transaction.
     *
     * @return An Observable that emits a boolean value indicating whether the credit operation was completed successfully (true) or canceled by the user (false).
     */
    private openCreditConfirmationDialog(): Observable<boolean> {
        this.validateOrderAndPaymentIds(this.orderId, this.paymentId);

        return this.dialog
            .open(CreditPaymentFormDialogComponent, {
                width: '400px',
                disableClose: true,
                data: {
                    orderId: this.orderId,
                    paymentId: this.paymentId,
                },
            })
            .afterClosed();
    }

    /**
     * Handles the crediting of a payment transaction that is in a creditable state.
     *
     * @returns An Observable that completes when the credit operation is finished, or is empty if the user cancels the action
     */
    private handleCreditableTransaction(): Observable<boolean> {
        return this.openCreditConfirmationDialog().pipe(
            switchMap((confirmed: boolean) => {
                if (confirmed) {
                    return of(confirmed);
                }

                // Terminate the stream without performing any additional actions since the
                // user canceled the credit operation.
                return EMPTY;
            }),
            tap(() => {
                this.params.onActionComplete();

                this.alertService.displaySuccessAlert(
                    CreditPaymentTransactionModalConstants.SUCCESS_MESSAGE,
                );
            }),
        );
    }

    private openSendInvoiceConfirmationDialog(): Observable<boolean> {
        if (!this.orderId) {
            throw new Error('Order ID is required to send an invoice.');
        }

        const title = this.invoiceSent
            ? SendInvoiceModalConstants.RESEND_TITLE
            : SendInvoiceModalConstants.SEND_TITLE;

        const content = this.invoiceSent
            ? SendInvoiceModalConstants.RESEND_CONTENT
            : SendInvoiceModalConstants.SEND_CONTENT;

        return this.dialog
            .open(ModalComponent, {
                disableClose: true,
                data: {
                    title,
                    content,
                    primaryAction: {
                        title: SendInvoiceModalConstants.PRIMARY_ACTION_TITLE,
                    },
                    isShowCancel: true,
                },
            })
            .afterClosed();
    }

    /**
     * Validates that both the order ID and the payment ID are present before attempting to perform void or credit operations.
     * If either value is missing, an error is thrown to prevent API calls with invalid parameters.
     */
    private validateOrderAndPaymentIds(
        orderId: string | null,
        paymentId: string | null | undefined,
    ): void {
        if (!orderId || !paymentId) {
            throw new Error(
                'Order ID and Payment ID are required to void/credit a payment.',
            );
        }
    }
}
